import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import { RefreshCw, BarChart3, Users, MousePointerClick, Clock, Target } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const RANGOS = [
  { label: 'Últimos 7 días', dias: 7 },
  { label: 'Últimos 30 días', dias: 30 },
  { label: 'Últimos 90 días', dias: 90 },
  { label: 'Último año', dias: 365 },
];

function fmtSeconds(s) {
  if (!s) return '0s';
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function fmtPercent(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function KpiCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function MarketingWebAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangoDias, setRangoDias] = useState(30);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingWebAPI.analytics({ dias: rangoDias });
      setData(res.data);
    } catch (e) {
      console.error('Error analytics:', e);
      setError(e?.response?.data?.detail || 'Error cargando analytics');
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      await marketingWebAPI.analyticsSync(7);
      await cargar();
    } catch (e) {
      console.error('Error sincronizando GA4:', e);
      setError(e?.response?.data?.detail || 'Error sincronizando GA4');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangoDias]);

  const lineChart = useMemo(() => {
    if (!data?.serie) return null;
    return {
      labels: data.serie.map((d) => d.fecha),
      datasets: [
        {
          label: 'Sesiones',
          data: data.serie.map((d) => d.sessions || 0),
          borderColor: '#1A9169',
          backgroundColor: 'rgba(26, 145, 105, 0.12)',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Usuarios',
          data: data.serie.map((d) => d.users || 0),
          borderColor: '#67CEA9',
          backgroundColor: 'rgba(103, 206, 169, 0.10)',
          fill: false,
          tension: 0.3,
        },
        {
          label: 'Conversiones',
          data: data.serie.map((d) => d.conversions || 0),
          borderColor: '#D7977E',
          backgroundColor: 'rgba(215, 151, 126, 0.15)',
          fill: false,
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  const pagesChart = useMemo(() => {
    if (!data?.top_pages?.length) return null;
    const top = data.top_pages.slice(0, 10);
    return {
      labels: top.map((p) => (p.path.length > 32 ? p.path.slice(0, 32) + '…' : p.path)),
      datasets: [{
        label: 'Pageviews',
        data: top.map((p) => p.pageviews),
        backgroundColor: '#1A9169',
      }],
    };
  }, [data]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">Analytics · GA4</h1>
          <p className="text-sm text-gray-500 mt-1">
            Datos de Google Analytics 4 de <span className="font-mono">duettocatering.com</span>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={rangoDias}
            onChange={(e) => setRangoDias(parseInt(e.target.value, 10))}
            className="px-3 py-2 text-sm border rounded-md"
          >
            {RANGOS.map((r) => (
              <option key={r.dias} value={r.dias}>{r.label}</option>
            ))}
          </select>
          <button
            onClick={cargar}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>
          <button
            onClick={sync}
            disabled={syncing}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
            title="Sincronizar los últimos 7 días desde GA4"
          >
            {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800">
          {error}. Verifica que GA4_PROPERTY_ID está configurado y que el Service Account tiene permiso Viewer en la propiedad.
        </div>
      )}

      {!data && !loading && !error && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-6 text-center text-sm text-blue-800">
          Aún no hay datos sincronizados. Pulsa <strong>Sincronizar ahora</strong> para traer los últimos 7 días desde GA4.
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <KpiCard label="Sesiones" value={(data.totales?.sessions ?? 0).toLocaleString('es-ES')} icon={BarChart3} />
            <KpiCard label="Usuarios" value={(data.totales?.users ?? 0).toLocaleString('es-ES')} icon={Users} />
            <KpiCard label="Pageviews" value={(data.totales?.pageviews ?? 0).toLocaleString('es-ES')} icon={MousePointerClick} />
            <KpiCard label="Conversiones" value={(data.totales?.conversions ?? 0).toLocaleString('es-ES')} icon={Target} />
            <KpiCard label="Engagement" value={fmtPercent(data.totales?.engagement_rate_medio)} />
            <KpiCard label="Bounce rate" value={fmtPercent(data.totales?.bounce_rate_medio)} />
            <KpiCard label="Tiempo sesión" value={fmtSeconds(data.totales?.avg_session_duration_s)} icon={Clock} />
          </div>

          {/* Gráfica diaria */}
          {lineChart && (
            <div className="bg-white border rounded-lg p-5 mb-6">
              <h2 className="text-base font-semibold mb-3 text-gray-700">Evolución diaria</h2>
              <div className="h-72">
                <Line
                  data={lineChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'top' } },
                    scales: { y: { beginAtZero: true } },
                  }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top páginas */}
            {pagesChart && (
              <div className="bg-white border rounded-lg p-5">
                <h2 className="text-base font-semibold mb-3 text-gray-700">Top 10 páginas (pageviews)</h2>
                <div className="h-80">
                  <Bar
                    data={pagesChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: { legend: { display: false } },
                      scales: { x: { beginAtZero: true } },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Top fuentes */}
            <div className="bg-white border rounded-lg p-5">
              <h2 className="text-base font-semibold mb-3 text-gray-700">Top fuentes de tráfico</h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fuente / canal</TableHead>
                    <TableHead className="text-right">Sesiones</TableHead>
                    <TableHead className="text-right">Usuarios</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.top_sources || []).slice(0, 12).map((s, i) => (
                    <TableRow key={`${s.source}-${i}`}>
                      <TableCell className="text-sm font-mono">{s.source}</TableCell>
                      <TableCell className="text-right text-sm">{s.sessions.toLocaleString('es-ES')}</TableCell>
                      <TableCell className="text-right text-sm">{s.users.toLocaleString('es-ES')}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.top_sources || data.top_sources.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="text-center text-gray-400 py-4">Sin datos</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
